import { assign, createMachine } from 'xstate';

import { AUTH_TOKEN_KEY } from './constants';
import { LogInWithOtp, RequestOtp } from './sso';

type ForgotPasswordContext = {
  error: unknown | undefined;
  email: string | undefined;
};

type ForgotPasswordEvent =
  | {
      type: 'request otp';
      email: string;
    }
  | {
      type: 'notify otp request failed';
      error: unknown;
    }
  | {
      type: 'notify otp request succeeded';
    }
  | {
      type: 'request otp again';
    }
  | {
      type: 'log in with otp';
      otp: string;
      callback?: (userId: number, token: string) => void;
    }
  | {
      type: 'notify log in succeeded';
      token: string;
      ssoUserId: number;
    }
  | {
      type: 'notify log in failed';
      error: unknown;
    };

export type ForgotPasswordState =
  | {
      value: 'idle' | 'requesting otp';
      context: { error: undefined; email: undefined };
    }
  | {
      value:
        | 'otp request succeeded'
        | 'logging in with otp'
        | 'log in succeeded';
      context: { error: undefined; email: string };
    }
  | {
      value: 'otp request failed';
      context: { error: unknown; email: undefined };
    }
  | {
      value: 'log in failed';
      context: { error: unknown; email: string };
    };

export function createForgotPasswordMachine({
  logInWithOtp,
  requestOtp,
  on,
}: {
  logInWithOtp: (params: {
    email: string;
    otp: string;
    token: string;
  }) => ReturnType<LogInWithOtp>;
  requestOtp: (params: {
    email: string;
    domain: string;
  }) => ReturnType<RequestOtp>;
  on: {
    loggedIn: (ssoUserId: number) => void;
    tokenObtained: (token: string, tokenObtained: number) => void;
  };
}) {
  return createMachine<
    ForgotPasswordContext,
    ForgotPasswordEvent,
    ForgotPasswordState
  >(
    {
      id: 'forgot-password',
      predictableActionArguments: true,
      preserveActionOrder: true,
      context: {
        error: undefined,
        email: undefined,
      },
      initial: 'idle',
      states: {
        idle: {
          on: {
            'request otp': {
              target: 'requesting otp',
            },
          },
        },
        'requesting otp': {
          entry: ['update email'],
          invoke: {
            src: 'get otp',
          },
          on: {
            'notify otp request failed': {
              target: 'otp request failed',
            },
            'notify otp request succeeded': {
              target: 'otp request succeeded',
            },
          },
        },
        'otp request failed': {
          entry: ['update error', 'clear email'],
          exit: ['clear error'],
          on: {
            'request otp': {
              target: 'requesting otp',
            },
          },
        },
        'otp request succeeded': {
          on: {
            'log in with otp': {
              target: 'logging in with otp',
            },
            'request otp again': {
              target: 'idle',
            },
          },
        },
        'logging in with otp': {
          invoke: {
            src: 'log in with otp',
          },
          on: {
            'notify log in failed': {
              target: 'log in failed',
            },
            'notify log in succeeded': {
              target: 'log in succeeded',
            },
          },
        },
        'log in failed': {
          entry: ['update error'],
          exit: ['clear error'],
          on: {
            'log in with otp': {
              target: 'logging in with otp',
            },
          },
        },
        'log in succeeded': {
          entry: ['notify of token obtained', 'notify of logged in'],
          type: 'final',
        },
      },
    },
    {
      services: {
        'get otp': (context, event) => async (send) => {
          try {
            if (event.type !== 'request otp') {
              throw new Error(`Unable to request OTP without an email`);
            }
            await requestOtp({ email: event.email, domain: '' });
            send({ type: 'notify otp request succeeded' });
          } catch (error) {
            console.error(
              `notify otp request failed: ${btoa(context.email || '')}`,
              error,
            );
            send({
              type: 'notify otp request failed',
              error,
            });
          }
        },
        'log in with otp': (context, event) => async (send) => {
          try {
            if (event.type !== 'log in with otp') {
              throw new Error(`Unable to log in without OTP`);
            }
            if (!context.email) {
              throw new Error(`Unable to log in with an email`);
            }
            const token = localStorage.getItem(AUTH_TOKEN_KEY) || '';
            const { token: accessToken, ssoUserId } = await logInWithOtp({
              email: context.email,
              otp: event.otp,
              token,
            });
            await event.callback?.(ssoUserId, accessToken);
            send({
              type: 'notify log in succeeded',
              token: accessToken,
              ssoUserId,
            });
          } catch (error) {
            console.error(
              `log in with otp failed: ${btoa(context.email || '')}`,
              error,
            );
            send({ type: 'notify log in failed', error });
          }
        },
      },
      actions: {
        'clear error': assign((context) => ({ ...context, error: undefined })),
        'update error': assign((context, event) =>
          event.type === 'notify otp request failed' ||
          event.type === 'notify log in failed'
            ? { ...context, error: event.error }
            : context,
        ),
        'clear email': assign((context) => ({ ...context, email: undefined })),
        'update email': assign((context, event) =>
          event.type === 'request otp'
            ? { ...context, email: event.email }
            : context,
        ),
        'notify of logged in': (context, event) => {
          if (event.type === 'notify log in succeeded') {
            if (context.email) {
              on.loggedIn(event.ssoUserId);
            }
          }
        },
        'notify of token obtained': (context, event) => {
          if (event.type === 'notify log in succeeded') {
            on.tokenObtained(event.token, event.ssoUserId);
          }
        },
      },
    },
  );
}
